<template>
  <layout>
    <page-header :header="$t('translation.translation')" />
    <div class="content">
      <b-card>
        <b-row>
          <b-col md="3">
            <b-form-input
              v-model="filter"
              :placeholder="$t('translation.search_by_key')"
            />
          </b-col>
          <b-col md="9">
            <locale-select v-model="selected" :locales="locales" class="mr-2" />
            <a :href="downloadCsv" target="_blank">
              <b-button variant="primary" @click="downloadCsv">
                {{ $t('translation.export_csv') }}
              </b-button>
            </a>
          </b-col>
        </b-row>
        <br />
        <div class="table-responsive">
          <b-table
            ref="table"
            :items="dictionary"
            :fields="fields"
            :per-page="perPage"
            :current-page="currentPage"
            :tbody-tr-class="['cursor-pointer']"
            hover
            striped
            @row-clicked="onTableRowClick"
          >
            <template slot="key" slot-scope="{ item }">
              <b>{{ item.group }}</b>
              <span>.{{ item.key }}</span>
            </template>
            <template slot="actions" slot-scope="row">
              <b-button size="sm" @click.self="row.toggleDetails">
                {{ $t('general.edit') }}
              </b-button>
            </template>

            <template slot="row-details" slot-scope="{ item }">
              <translation-edit
                :item="item"
                :locales="selectedLocales"
                @submit="onSubmit"
              />
            </template>
          </b-table>
        </div>
        <div class="flex mt-4">
          <div class="w-16 mr-2">
            <b-form-select v-model="perPage" :options="perPageOptions" />
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="perPage ? dictionary.length : 1"
            :per-page="perPage"
          />
        </div>
      </b-card>
    </div>
  </layout>
</template>

<script>
import TranslationEdit from './TranslationEdit'
import LocaleSelect from './LocaleSelect'
import filter from 'lodash-es/filter'

export default {
  name: 'TranslationsIndex',

  components: {
    TranslationEdit,
    LocaleSelect,
  },

  data() {
    return {
      dictionaries: [],
      locales: [],
      selected: [],
      filter: '',
      fields: [],
      perPage: 15,
      currentPage: 1,
      perPageOptions: [
        { value: 15, text: 15 },
        { value: 30, text: 30 },
        { value: 60, text: 60 },
        { value: 0, text: this.$t('translation.all_translations') },
      ],
    }
  },

  computed: {
    downloadCsv() {
      const { hostname, protocol } = window.location
      return `${protocol}//${hostname}/api/translations/csv`
    },
    selectedLocales() {
      return filter(
        this.locales,
        locale => this.selected.indexOf(locale.locale) !== -1,
      )
    },
    dictionary() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      const selected = this.selected.sort((first, second) =>
        first.localeCompare(second),
      )
      return this.dictionaries
        .filter(word => {
          const filterLowercase = this.filter.trim().toLowerCase()
          for (const key in word.values) {
            const value = word.values[key]
            if (value && value.toLowerCase().indexOf(filterLowercase) > -1) {
              return true
            }
          }
          return (
            `${word.group}.${word.key}`.toLowerCase().indexOf(filterLowercase) >
            -1
          )
        })
        .map(word => {
          const newValue = {}
          for (const locale of selected) {
            newValue[locale] = word.values[locale]
          }
          return { ...word, ...newValue }
        })
    },
  },

  mounted() {
    this.fetchLocales()
      .then(response => {
        this.locales = response.data
        if(!this.selected.length)
          this.selected = this.locales.map(a => a.locale)
        this.initColumns()
        return this.fetchDictionary(this.selected)
      })
      .then(response => {
        this.dictionaries = response.data
        this.startWatchers()
      })
  },

  methods: {
    fetchLocales() {
      return this.$http.get('/translations/locales?all_locales=y')
    },

    fetchDictionary(langs) {
      return this.$uiBlocker(
        this.$http.get('/translations/strings', {
          params: { locales: langs.join(',') },
        }),
        this.$refs.table.$el,
      )
    },

    startWatchers() {
      this.$watch('selected', (newLocales, oldLocales) => {
        if (newLocales === oldLocales) {
          return
        }

        this.reload()
        this.initColumns()
      })
    },

    initColumns() {
      const newFields = [
        {
          key: 'key',
          label: this.$t('translation.key'),
        },
      ]

      for (const lang of this.selectedLocales) {
        newFields.push({
          key: lang.locale,
          label: lang.name,
        })
      }

      newFields.push({
        key: 'actions',
        label: this.$t('translation.actions'),
      })
      this.fields = newFields
    },

    onTableRowClick(item) {
      this.$set(item, '_showDetails', !item._showDetails)
    },

    onSubmit(item) {
      const values = {}
      for (const locale of this.locales) {
        if (item[locale.locale]) values[locale.locale] = item[locale.locale]
      }

      const promise = this.$http
        .put(`translations/strings/${item.id}`, { values })
        .then(({ data }) => {
          this.dictionaries = this.dictionaries.map(word =>
            word.id === data.id ? data : word,
          )
        })
        .catch(err => this.$toast.error(err.response.message))

      this.$uiBlocker(promise, this.$refs.table.$el)
    },

    async reload() {
      const response = await this.$uiBlocker(
        this.fetchDictionary(this.selected),
        this.$refs.table.$el,
      )
      this.dictionaries = response.data
    },
  },
}
</script>
